/** Presentation helpers for the Import Runs grid + detail dialog. */
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

/** IMPORT_RUN_STATES labels, 1-based (state 1 → index 0). Contract-tested
 *  against the spec's `x-status-labels`. */
export const IMPORT_STATUS_LABELS = [
  'Created',
  'Rejected before processing',
  'Currently processing',
  'Import successful',
  'Imported with errors',
  'Import failure',
  'Group processed',
  'Superseded before processing',
];

/** Import_Run.ORIGINS labels, 0-based. Contract-tested against the spec's
 *  `x-origin-labels`. */
export const IMPORT_ORIGIN_LABELS = [
  'Unknown',
  'Import API',
  'Integration',
  'Dashboard',
  'CLI',
  'URL',
  'Rectification',
  'Preprocessed XML',
  'Tag API',
  'Departure update',
  'Split OTA bookings',
  'AI import',
  'Console',
  'Booking reimport',
];

export const IMPORT_STATE = {
  CREATED: 1,
  REJECTED: 2,
  PROCESSING: 3,
  SUCCESS: 4,
  PARTIAL_SUCCESS: 5,
  FAILURE: 6,
  GROUP_INITIATED: 7,
  // Catalog-lane coalescing: an older queued snapshot skipped in favour of a
  // newer one for the same lane_key. Benign — renders with the default chip.
  SUPERSEDED: 8,
} as const;

export function importStatusLabel(state: number): string {
  return IMPORT_STATUS_LABELS[state - 1] ?? `State ${state}`;
}

export function importOriginLabel(origin: number): string {
  return IMPORT_ORIGIN_LABELS[origin] ?? `Origin ${origin}`;
}

function stateColor(state: number): ChipProps['color'] {
  switch (state) {
    case IMPORT_STATE.SUCCESS:
    case IMPORT_STATE.GROUP_INITIATED:
      return 'success';
    case IMPORT_STATE.PARTIAL_SUCCESS:
      return 'warning';
    case IMPORT_STATE.FAILURE:
    case IMPORT_STATE.REJECTED:
      return 'error';
    case IMPORT_STATE.PROCESSING:
      return 'info';
    default:
      return 'default';
  }
}

export function ImportStateChip({ state }: { state: number }) {
  const color = stateColor(state);
  return (
    <Chip
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      label={importStatusLabel(state)}
      sx={{ height: 22, fontSize: 12 }}
    />
  );
}

/** First 8 hex chars of a UUID — enough to eyeball, full value via copy. */
export function shortId(id: string | null): string {
  return id ? id.slice(0, 8) : '—';
}
